import {
  Grid,
  Card,
  Skeleton,
  Stack,
  Typography,
  Divider,
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
} from "@mui/material";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import notifiers from "global/constants/NotificationConstants";
import {
  getFormattedStatsCount,
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import { planLimits } from "models/interfaces";
import checkOutStyles from "screens/Settings/MyPlan/Checkout/Checkout.styles";
import { cardStyle } from "screens/Settings/MyPlan/Components/AddPaymentMethodDialog";

interface CustomProps {
  planFeatures: any;
  cardUserDetails: any;
  setCardUserDetails: Function;
  userName: string;
  isLoading: boolean;
  agreeTerms: boolean;
  setAgreeTerms: Function;
  authorizeCharge: boolean;
  setAuthorizeCharge: Function;
  isPayNowEnabled: any;
  setPaymentMethodId: Function;
  finalUserActivation: Function;
  apiLoader: boolean;
  setApiLoader: Function;
  setConfirmedPaymentMethod: Function;
  handleValidation: Function;
}

const Checkout = (props: CustomProps) => {
  const {
    isLoading,
    planFeatures,
    cardUserDetails,
    agreeTerms,
    setAgreeTerms,
    authorizeCharge,
    setAuthorizeCharge,
    isPayNowEnabled,
    setPaymentMethodId,
    finalUserActivation,
    apiLoader,
    setApiLoader,
    setConfirmedPaymentMethod,
    handleValidation,
  } = props;
  const classes = checkOutStyles;

  const stripe = useStripe();
  const elements = useElements();

  const handleCreatePM = async () => {
    try {
      setApiLoader(true);
      if (handleValidation() && stripe !== null && elements !== null) {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card: elements.getElement(CardElement)!,
          billing_details: {
            name: cardUserDetails.cardName.value,
            email: cardUserDetails.userEmail.value,
          },
        });
        if (error) {
          openErrorNotification(
            isTruthy(error) ? error.message : notifiers.GENERIC_ERROR
          );
          setApiLoader(false);
        } else {
          setApiLoader(false);
          setPaymentMethodId(paymentMethod.id);
          setConfirmedPaymentMethod(true);
        }
      }
      setApiLoader(false);
    } catch (error: any) {
      setApiLoader(false);
      handleErrorMessage(error);
    }
  };

  const getSkeleton = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Card>
          <Skeleton animation="wave" variant="rectangular" height={150} />
        </Card>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Card>
          <Skeleton animation="wave" variant="rectangular" height={150} />
        </Card>
      </Grid>
    </Grid>
  );

  const getCheckoutBody = () => {
    return (
      <Grid container spacing={2} mt={5}>
        {isLoading
          ? getSkeleton
          : isTruthy(planFeatures) && (
              <Grid item xs={12} sm={6}>
                <Card>
                  <Stack direction="column" spacing={2} p={2.5}>
                    <Stack>
                      <Typography variant="h6" color="text.primary">
                        Billing Information
                      </Typography>
                    </Stack>
                    <Stack>
                      <Typography variant="body2">
                        {cardUserDetails?.cardName?.value}
                      </Typography>
                      <Typography variant="body2">
                        {cardUserDetails?.userEmail?.value}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Divider
                    sx={{
                      width: "100%",
                      borderColor: " #47556940",
                    }}
                  />
                  <Stack direction="column" spacing={2} p={2.5}>
                    <Stack
                      sx={{
                        p: 2.5,
                        borderRadius: "8px",
                        border: "1px solid #47556940",
                      }}
                    >
                      <Typography variant="h6" color="text.primary">
                        Payment Method
                      </Typography>
                      <Box
                        sx={{
                          border: "#47556940 solid 1px",
                          borderRadius: "9px",
                          padding: "12px",
                          backgroundColor: "#fff",
                          mt: 1,
                        }}
                      >
                        <CardElement options={cardStyle} id="card_number" />
                      </Box>
                    </Stack>

                    <Stack
                      direction="column"
                      spacing={2}
                      alignItems="center"
                      pt={2}
                      justifyContent="center"
                    >
                      <Button
                        variant="contained"
                        onClick={() => handleCreatePM()}
                      >
                        Confirm Payment Method
                      </Button>
                      <Stack spacing={2} pt={2}>
                        <Box
                          sx={{
                            p: 2,
                            background: "#F7FFF2",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="#47AA12"
                            textAlign={"center"}
                          >
                            Your billing information will be encrypted and is
                            100% safe with out payment processing partner.
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </Stack>
                </Card>
              </Grid>
            )}

        {isLoading
          ? getSkeleton()
          : isTruthy(planFeatures) && (
              <Grid item xs={12} sm={6}>
                <Card sx={{ p: 2.5 }}>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography variant="h6" color="text.primary">
                        Purchase Summary
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack pt={2} spacing={2} direction="column">
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="h6" color="text.primary">
                        {planFeatures?.name}
                      </Typography>
                      <Typography variant="h6" color="text.primary">
                        {planFeatures.currency === "INR"
                          ? `₹${getFormattedStatsCount(
                              planFeatures?.price ?? 0
                            )}`
                          : `$${getFormattedStatsCount(
                              planFeatures?.price ?? 0
                            )}`}
                      </Typography>
                    </Stack>
                    <Stack>
                      {planFeatures?.limits
                        ?.filter((item: any) => item.entity !== "dailyEmail")
                        .map((item: planLimits) => (
                          <Typography variant="body2">
                            {item.entity === "email"
                              ? `${getFormattedStatsCount(
                                  item?.limit
                                )} emails / month`
                              : `${getFormattedStatsCount(item?.limit)} ${
                                  item.entity
                                }s`}
                          </Typography>
                        ))}
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="h6" color="text.primary">
                        Tax
                      </Typography>
                      <Typography variant="h6" color="text.primary">
                        {planFeatures.currency === "INR" ? `₹0.00}` : `$0.00`}
                      </Typography>
                    </Stack>
                    <Divider
                      sx={{
                        pt: 1,
                        borderColor: " #47556940",
                      }}
                    />
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                      pt={1}
                    >
                      <Typography variant="h6" color="text.primary">
                        Subtotal
                      </Typography>
                      <Typography variant="h6" color="text.primary">
                        {planFeatures.currency === "INR"
                          ? `₹${getFormattedStatsCount(
                              planFeatures?.price ?? 0
                            )}`
                          : `$${getFormattedStatsCount(
                              planFeatures?.price ?? 0
                            )}`}
                      </Typography>
                    </Stack>
                    <Divider
                      sx={{
                        pt: 1,
                        borderColor: " #47556940",
                      }}
                    />
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                      pt={1}
                    >
                      <Typography variant="h6" color="text.primary">
                        Total amount due
                      </Typography>
                      <Typography variant="h6" color="text.primary">
                        {planFeatures.currency === "INR"
                          ? `₹${getFormattedStatsCount(
                              planFeatures?.price ?? 0
                            )}`
                          : `$${getFormattedStatsCount(
                              planFeatures?.price ?? 0
                            )}`}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      pt={2}
                    >
                      <Checkbox
                        sx={{
                          padding: "0px !important",
                        }}
                        checked={agreeTerms}
                        onChange={(e) => setAgreeTerms(e.target.checked)}
                      />
                      <Typography display="flex" variant="subtitle2">
                        I agree to the terms of use and privacy policy of
                        Mailzzy.
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Checkbox
                        sx={{
                          padding: "0px !important",
                        }}
                        checked={authorizeCharge}
                        onChange={(e) => setAuthorizeCharge(e.target.checked)}
                      />
                      <Typography display="flex" variant="subtitle2">
                        I authorize Mailzzy to automatically charge me every
                        month based on the subscription.
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      pt={3}
                      justifyContent="center"
                    >
                      <Button
                        variant="contained"
                        disabled={!isPayNowEnabled()}
                        onClick={() =>
                          finalUserActivation(true, planFeatures.id, undefined)
                        }
                      >
                        Pay Now
                      </Button>
                    </Stack>
                  </Stack>
                </Card>
              </Grid>
            )}
      </Grid>
    );
  };

  const getPaymentInformation = () => {
    return (
      <Grid container spacing={2} mt={2}>
        <Grid item xs={12}>
          <Stack bgcolor="#47556908">
            <List sx={classes.predefinedListStyle}>
              <ListItem sx={classes.predefinedListItem}>
                <Typography variant="body2" color="text.primary">
                  We cannot refund once you purchased a subscription, but you
                  can always cancel 
                </Typography>
              </ListItem>
              <ListItem sx={classes.predefinedListItem}>
                <Typography variant="body2" color="text.primary">
                  <span style={{ fontWeight: 700 }}>Auto-renewal terms:</span>{" "}
                  Your subscription will automatically renew each month unless
                  you cancel. Your monthly fee may increase depending on your
                  contact tier level and email sends. You may call customer
                  support, prior to your renewal, to cancel your account. By
                  submitting your payment you acknowledge that you have read and
                  agree to our auto-renewal terms, the Terms of Service,
                  cancellation policy, and acknowledge receipt of our privacy
                  notice. 
                </Typography>
              </ListItem>
            </List>
          </Stack>
        </Grid>
      </Grid>
    );
  };
  return (
    <>
      {getCheckoutBody()}
      {isLoading ? (
        <Skeleton animation="wave" variant="text" height={150} />
      ) : (
        getPaymentInformation()
      )}
      <CustomLoader isLoading={apiLoader} />
    </>
  );
};

export default Checkout;
